import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChevronRight from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem as MuiMenuItem,
  PopoverProps,
  styled,
  Switch,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { SubNavBar } from "components/common/SubNavBar";
import { HEADER_MENU_HEIGHT } from "config/constants";
import { createUndoHotkey } from "hooks/useHotkeys/useHotkeys";
import { UndoButton } from "new-components/buttons/UndoButton";
import { Typography } from "new-components/typography/Typography";
import { HeaderModeChoices, HeaderTitles } from "pages/review/ReviewPage";
import { Detector } from "types/detectors";
import { DetectorSummary, GroupSummary, ReviewPageSummaryData } from "types/imageQueries";
import { User, UserType } from "types/users";

import { ReviewPageSubNavBarQueryDetailsMenu } from "./components/ReviewPageSubNavBarQueryDetailsMenu";

const GLSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 29,
  padding: "7px 0",
  marginRight: 7,
  "& .MuiSwitch-switchBase": {
    margin: 0,
    padding: 0,
    transform: "translate(0px, 5px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translate(23px, 5px)",
      "& .MuiSwitch-thumb:before": {
        backgroundColor: theme.palette.axon.main,
        borderRadius: 10,
      },
      "& + .MuiSwitch-track": {
        opacity: 0.2,
        backgroundColor: theme.palette.axon.main,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: "#91959b",
    width: 19,
    height: 19,
    "&::before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
    },
  },
  "& .MuiSwitch-track": {
    width: 56,
    height: 15,
    opacity: 1,
    backgroundColor: "#e5e9ef",
    borderRadius: 10,
  },
}));

export interface ReviewPageSubNavBarProps {
  mode: string;
  user: User;
  detector: Detector | undefined;
  canUndo: boolean;
  isLabeling: boolean;
  imageQueryId?: string;
  labelerActive: boolean;
  dataForHeaderSelection: ReviewPageSummaryData | undefined;
  undoLabel: () => void;
  handleLabelStatusChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const MenuItem = styled(MuiMenuItem)(() => ({
  justifyContent: "space-between",
  lineHeight: "24px",
  fontSize: "18px",
  padding: "10px 20px",
  paddingRight: "10px",
  minWidth: "265px",
}));

const MENU_ANCHOR_ORIGIN: PopoverProps["anchorOrigin"] = { vertical: "top", horizontal: "right" };

interface CustomMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  anchorOrigin?: PopoverProps["anchorOrigin"];
  children: React.ReactNode;
}

const CustomMenu = (props: CustomMenuProps) => {
  const { anchorEl, open, onClose, anchorOrigin, children } = props;

  return (
    <Menu
      anchorOrigin={anchorOrigin}
      anchorEl={anchorEl}
      open={open || false}
      onClose={onClose}
      MenuListProps={{ sx: { padding: 0 } }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: (HEADER_MENU_HEIGHT - 4) * 7,
            boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
            borderRadius: 0,
          },
        },
      }}
    >
      {children}
    </Menu>
  );
};

export const ReviewPageSubNavBarNew = (props: ReviewPageSubNavBarProps) => {
  const {
    mode,
    user,
    detector,
    canUndo,
    isLabeling,
    imageQueryId,
    labelerActive,
    dataForHeaderSelection,
    undoLabel,
    handleLabelStatusChange,
  } = props;

  const [searchParams, setSearchParams] = useSearchParams();

  const paramDetectorId = searchParams.get("detector_id");
  const detectorId = detector?.id || paramDetectorId;
  const groupId = searchParams.get("group_id") || "";

  const selectedDetectorName =
    dataForHeaderSelection?.detectors?.find((det: DetectorSummary) => det.predictor__id === detectorId)
      ?.predictor__name ||
    detector?.name ||
    "";

  const selectedGroupName =
    dataForHeaderSelection?.groups?.find((group: GroupSummary) => group.predictor__set__id === groupId)
      ?.predictor__set__name || "";

  const detailDetectorLink = `/reef/detectors/${detectorId || ""}`;

  const isUserLabeler = user.userprofile.customer_type === UserType.Labeler;
  const isUserLabelerManager = user.userprofile.customer_type === UserType.Manager;
  const isUserSuperuser = user.is_superuser || user.userprofile.customer_type === UserType.Groundlight;

  const isUserCustomer = !isUserLabelerManager && !isUserLabeler && !isUserSuperuser;

  const enableReviewOptions = user.enable_review_options;
  const onlyLabelerUi = user.only_labeler_ui;

  const [optionsAnchorEl, setOptionsAnchorEl] = React.useState<null | HTMLElement>(null);
  const [detectorAnchorEl, setDetectorAnchorEl] = React.useState<null | HTMLElement>(null);
  const [groupAnchorEl, setGroupAnchorEl] = React.useState<null | HTMLElement>(null);
  const [imageQueryAnchorEl, setImageQueryAnchorEl] = useState<HTMLElement | null>(null);

  const optionsMenuOpen = Boolean(optionsAnchorEl);
  const detectorMenuOpen = Boolean(detectorAnchorEl);
  const groupMenuOpen = Boolean(groupAnchorEl);

  // Undo hotkey setup
  const { useUndoHotkey } = createUndoHotkey(canUndo, isLabeling, undoLabel);
  useUndoHotkey();

  const title = useMemo(() => {
    const Loading = <CircularProgress size={23} style={{ marginTop: "5px", marginRight: "5px", color: "white" }} />;

    if (mode === HeaderModeChoices.Detector) {
      return selectedDetectorName || Loading;
    }
    if (mode === HeaderModeChoices.Group) {
      return selectedGroupName || Loading;
    }

    return HeaderTitles[mode as keyof typeof HeaderTitles];
  }, [mode, selectedDetectorName, selectedGroupName]);

  const menuHandler = (isUnclearMode: boolean) => {
    let params = {};
    if (isUnclearMode) params = { unclear_only: true };
    setSearchParams(params);
    handleOptionsMenuClose();
  };

  const handleDetectorMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDetectorAnchorEl(event.currentTarget);
  };

  const handleDetectorMenuClose = () => {
    setDetectorAnchorEl(null);
  };

  const handleGroupMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setGroupAnchorEl(event.currentTarget);
  };

  const handleGroupMenuClose = () => {
    setGroupAnchorEl(null);
  };

  const handleOptionsMenuButtonOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOptionsAnchorEl(event.currentTarget);
  };

  const handleOptionsMenuClose = () => {
    setOptionsAnchorEl(null);
    handleDetectorMenuClose();
    handleGroupMenuClose();
  };

  const managerItems = [
    <MenuItem onClick={(event) => setImageQueryAnchorEl(event.currentTarget)} key="view_query_details">
      {HeaderTitles[HeaderModeChoices.ViewQueryDetails]}
      <ChevronRight fontSize="small" />
    </MenuItem>,
    <ReviewPageSubNavBarQueryDetailsMenu
      anchorEl={imageQueryAnchorEl}
      onClose={() => setImageQueryAnchorEl(null)}
      detectorId={detectorId || undefined}
      imageQueryId={imageQueryId}
      key="view_query_details_menu"
    />,
  ];

  const customerItems = [
    <MenuItem onClick={() => menuHandler(true)} key="by_unclear">
      {`${HeaderTitles[HeaderModeChoices.Unclear]} ${typeof dataForHeaderSelection?.unsure === "number" ? `(${dataForHeaderSelection?.unsure})` : ""
        }`}
    </MenuItem>,
    <MenuItem onClick={handleDetectorMenuOpen} key="by_detector">
      Review By Detector
      <KeyboardArrowRightIcon width={20} height={20} />
    </MenuItem>,
    <CustomMenu
      anchorEl={detectorAnchorEl}
      anchorOrigin={MENU_ANCHOR_ORIGIN}
      open={detectorMenuOpen}
      onClose={handleDetectorMenuClose}
    >
      {dataForHeaderSelection?.detectors &&
        dataForHeaderSelection.detectors.map((detector: DetectorSummary) => (
          <MenuItem
            onClick={() => setSearchParams({ detector_id: detector.predictor__id })}
            key={detector.predictor__id}
          >
            {`(${detector.num_image_queries || 0}) ${detector.predictor__name || 0} (${detector.predictor__id.slice(
              -6
            )})`}
          </MenuItem>
        ))}
    </CustomMenu>,
    <MenuItem onClick={handleGroupMenuOpen} key="by_group">
      Review By Group
      <KeyboardArrowRightIcon width={20} height={20} />
    </MenuItem>,
    <CustomMenu
      anchorEl={groupAnchorEl}
      anchorOrigin={MENU_ANCHOR_ORIGIN}
      open={groupMenuOpen}
      onClose={handleGroupMenuClose}
    >
      {dataForHeaderSelection?.groups &&
        dataForHeaderSelection.groups.map((group: GroupSummary) => (
          <MenuItem
            onClick={() => setSearchParams({ group_id: group.predictor__set__id })}
            key={group.predictor__set__id}
          >
            {`${group.predictor__set__name} (${group.num_image_queries})`}
          </MenuItem>
        ))}
    </CustomMenu>,
  ];

  return (
    <SubNavBar>
      <Box sx={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {!onlyLabelerUi && (
            <Link to={detailDetectorLink}>
              <IconButton className="p-1 hidden sm:inline-block pt-2" aria-label="back2">
                <ArrowBackIosNewIcon className="text-white" />
              </IconButton>
            </Link>
          )}
          <Button
            aria-label="more"
            id="select-detector-button"
            aria-controls={optionsMenuOpen ? "long-menu" : undefined}
            aria-expanded={optionsMenuOpen ? "true" : undefined}
            aria-haspopup="true"
            onClick={handleOptionsMenuButtonOpen}
            disabled={isUserSuperuser || isUserLabeler}
            endIcon={
              isUserCustomer || isUserLabelerManager ? (
                <KeyboardArrowDownIcon width={20} height={20} />
              ) : undefined
            }
            sx={{ lineBreak: "anywhere", color: "white", width: "auto", "&:disabled": { color: "white" } }}
          >
            <Typography variant="subtitle" fontWeight="bold" textTransform="uppercase">
              {title}
            </Typography>
          </Button>
          <CustomMenu
            open={optionsMenuOpen || false}
            anchorEl={optionsAnchorEl}
            onClose={handleOptionsMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <MenuItem onClick={() => menuHandler(false)}>{HeaderTitles[HeaderModeChoices.Global]}</MenuItem>

            {managerItems}

            {enableReviewOptions && isUserCustomer && customerItems}
          </CustomMenu>
        </Box>

        {isLabeling && canUndo && (
          <Box sx={{ width: "220px" }}>
            <UndoButton onClick={undoLabel} enableUndo={canUndo} />
          </Box>
        )}

        {onlyLabelerUi && (
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Typography variant="small" sx={{ width: "60px" }}>
              LABELER STATUS:
            </Typography>
            <FormGroup sx={{ width: "110px" }}>
              <FormControlLabel
                sx={{ margin: 0 }}
                control={
                  <GLSwitch
                    checked={labelerActive}
                    onChange={handleLabelStatusChange}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={labelerActive ? "Active" : "Inactive"}
              />
            </FormGroup>
          </Box>
        )}
      </Box>
    </SubNavBar>
  );
};
